import { Box, useDisclosure } from "@caspeco/casper-ui-library";
import { useDayStates } from "api/api-hooks/use-day-states";
import { useExternalBookingSettings } from "api/api-hooks/use-external-booking-settings";
import { BookingValidator } from "components/booking-validator";
import ErrorBoundary from "components/error-boundary/error-boundary";
import ReservationFailedModal from "components/error-components/reservation-failed-modal";
import { ErrorView } from "components/pages/error-view";
import { ExperienceViewFilterInput } from "components/pages/experience-view/experience-view-filter-input";
import { Stepper } from "components/stepper/stepper";
import { PreorderProvider } from "context/preorder-provider";
import { useBookingId } from "hooks/use-booking-id";
import { useBookingStateContext } from "hooks/use-booking-state";
import { useEffect } from "react";
import { Outlet, useParams } from "react-router-dom";
import { Routes } from "routes";
import { getSearchParams } from "search-params";
import { BookingGuard } from "store/booking-guard";
import { IsoCountryCode } from "types/iso-country-code";

const isInitialized = false;
function init() {
    if (isInitialized) return;

    const { system } = getSearchParams();

    const systemParts = system?.split("_");
    const [SYSTEM_PREFIX] = systemParts ?? [];

    if (
        SYSTEM_PREFIX &&
        !Object.values(IsoCountryCode).includes(SYSTEM_PREFIX.toLocaleUpperCase())
    ) {
        // Antiviruses scanning emails, ROT-13 encrypts system- and other GET-parameters resulting in spammy requests to MARC
        throw new Error(`Invalid system prefix in: ${system}`);
    }
}
init();

const BookingFlowLayout = () => {
    const externalBookingSettings = useExternalBookingSettings();
    const { system, unitId } = useParams();
    const { selectedMonth, isActivityBooking, isBookingRequest } = useBookingStateContext();
    const bookingId = useBookingId();
    const { isExperienceViewMode } = getSearchParams();

    const { isOpen: isOpenFilter, onOpen: onOpenFilter, onClose: onCloseFilter } = useDisclosure();

    const dateForDayStates = selectedMonth instanceof Date ? selectedMonth : new Date();
    // Prefetch under the same mode the calendar reads, so the booking-request
    // flow doesn't warm (and pay for) the heavier standard-mode query it never uses
    useDayStates(dateForDayStates, !!isBookingRequest);

    useEffect(() => {
        if (system && unitId) {
            localStorage.setItem("lastKnownSystemId", system);
            localStorage.setItem("lastKnownUnitId", unitId);
            localStorage.setItem(
                "lastBookingRouteType",
                isActivityBooking ? Routes.ACTIVITY_BOOKING : "standard"
            );
            localStorage.setItem("lastKnownIsExperienceView", String(isExperienceViewMode));
        }
    }, [system, unitId, isActivityBooking, isExperienceViewMode]);

    if (externalBookingSettings.isError) {
        return <ErrorView error={externalBookingSettings.error} />;
    }

    return (
        <PreorderProvider>
            <ErrorBoundary fallback={<ErrorView />}>
                <Box className="webBookingContainer" flex={1} w="full">
                    <ReservationFailedModal />
                    {!isBookingRequest && isExperienceViewMode ? (
                        <ExperienceViewFilterInput
                            isOpenFilter={isOpenFilter}
                            onOpenFilter={onOpenFilter}
                            onCloseFilter={onCloseFilter}
                        />
                    ) : (
                        <Stepper />
                    )}
                    <BookingGuard onOpenFilter={onOpenFilter} />
                    <Outlet />
                    {bookingId && <BookingValidator />}
                </Box>
            </ErrorBoundary>
        </PreorderProvider>
    );
};

export default BookingFlowLayout;
